import { Card, CardContent } from '@ui/card.tsx';
import { FileUploader } from '@r/components/import/file-uploader.tsx';
import { CsvFile } from '@r/components/import/csv-file.tsx';
import { ImportErrorPage } from '@r/components/import/import-error-page.tsx';
import { unparse } from 'papaparse';
import { useFileParserWithErrors } from '@r/hooks/use-file-parser-patch-with-errors.ts';
import { usersImportBody } from '@r/api/generated-zod/index.ts';
import { Trans, useTranslation } from 'react-i18next';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { useImportState } from '@r/hooks/use-import-state.ts';
import { toast } from 'sonner';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Button } from '@r/components/ui/button.tsx';
import { UserPlus, Plus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { CreateNewUserDialog } from '../../components/forms/users/create-new-user-dialog.tsx';
import { useNavigate, useSearch } from '@tanstack/react-router';
import type { Option } from '@r/types/index.ts';
import { z } from 'zod';

const COMMENT_MAX_LENGTH = 255;

type UsersImportSearchParams = {
  openCreateContact?: boolean | string;
  companyUniqueCode?: string;
  companyName?: string;
  [key: string]: unknown;
};

export default function UsersImport() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as UsersImportSearchParams;
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const { state, setErrorState, setPreviewState, resetState } = useImportState('users');

  // Utiliser le schéma Zod généré par Orval pour les données d'import d'utilisateurs
  // Le schéma usersImportBody a la structure { data: [...] }
  // Chaque item du tableau correspond à une ligne CSV
  const usersImportSchema = usersImportBody.shape.data.element.extend({
    userWorkspace: usersImportBody.shape.data.element.shape.userWorkspace.extend({
      comment: z
        .string()
        .max(
          COMMENT_MAX_LENGTH,
          t('admin.user.profile.validation.comment.max.length', { max: COMMENT_MAX_LENGTH })
        )
        .nullish()
    })
  });
  const { onParse } = useFileParserWithErrors(usersImportSchema, '/admin/users/import');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const initialCompanyOption: Option | null =
    typeof search.companyUniqueCode === 'string' &&
    search.companyUniqueCode.length > 0 &&
    typeof search.companyName === 'string' &&
    search.companyName.length > 0
      ? { value: search.companyUniqueCode, label: search.companyName }
      : null;

  useEffect(() => {
    const shouldOpenFromSearch =
      search.openCreateContact === true || search.openCreateContact === 'true';
    if (!shouldOpenFromSearch) return;
    setIsModalOpen(true);
  }, [search.openCreateContact]);

  const csv = unparse({
    fields: [
      'email',
      'plainPassword',
      'firstname',
      'lastname',
      'companyUniqueCode',
      'uniqueCode',
      'maxRole',
      'phone',
      'comment',
      'emailNotification'
    ],
    data: [
      [
        'p.martin@fiddy.app',
        'Password123?',
        'Pierre',
        'Martin',
        'companyCode',
        'MP01',
        'ROLE_MEMBER',
        '06.00.00.00.00',
        'Commentaire exemple',
        'Yes'
      ],
      [
        'y.dupuis@fiddy.app',
        'Password123?',
        'Yohan',
        'Dupuis',
        'companyCode',
        'DY09',
        'ROLE_MANAGER',
        '0600000000',
        'Commentaire exemple',
        'No'
      ],
      [
        'm.bernard@fiddy.app',
        'Password123?',
        'Mathieu',
        'Bernard',
        'companyCode',
        'BM02',
        'ROLE_SALES_ADMIN',
        '+33600000000',
        'Commentaire exemple',
        'Yes'
      ],
      [
        'c.admin@fiddy.app',
        'Password123?',
        'Camille',
        'Admin',
        '',
        'CA01',
        'ROLE_ADMIN',
        '',
        '',
        'No'
      ],
      [
        'c.thomas@fiddy.app',
        'Password123?',
        'Cyril',
        'Thomas',
        '',
        'TC01',
        'ROLE_ADMIN',
        '',
        '',
        'No'
      ],
      [
        'h.petit@fiddy.app',
        'Password123?',
        'Hugo',
        'Petit',
        '',
        'PH01',
        'ROLE_FIDDY_ADMIN',
        '',
        '',
        'No'
      ]
    ]
  });

  const filename = sanitizedWorkspaceName
    ? `${sanitizedWorkspaceName}_users_import_example.csv`
    : 'users_import_example.csv';

  const handleParse = async (files: File[]) => {
    const toastId = toast.loading(t('import.process.analyzing'));

    try {
      const result = await onParse(files);

      if (result.isPreview) {
        toast.dismiss(toastId);
        setPreviewState(result.errors ?? [], files[0], result.analysis ?? null);
      } else if (result.success) {
        toast.success(result.message || t('import.process.success'), { id: toastId });
        resetState();
      } else if (result.errors && result.errors.length > 0) {
        toast.dismiss(toastId);
        setErrorState(result.errors, files[0]);
      }
    } catch (error) {
      showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
        id: toastId
      });
    }
  };

  const handleRetry = async () => {
    if (state.originalFile) {
      const toastId = toast.loading(t('import.process.loading'));

      try {
        const result = await onParse([state.originalFile], true);

        if (result.success) {
          toast.success(result.message || t('import.process.success'), { id: toastId });
          resetState();
        } else {
          toast.error(result.message || t('import.process.error'), { id: toastId });
          if (result.errors && result.errors.length > 0) {
            setErrorState(result.errors, state.originalFile);
          }
        }
      } catch (error) {
        showOrvalErrorToast(error, typeof error === 'string' ? error : t('import.process.error'), {
          id: toastId
        });
      }
    }
  };

  if (state.isPreview && state.originalFile) {
    return (
      <ImportErrorPage
        errors={state.errors}
        originalFile={state.originalFile}
        onRetry={handleRetry}
        onNewFile={resetState}
        importType="users"
        analysis={state.analysis}
      />
    );
  }

  return (
    <div className="space-y-10">
      <h1 className="text-4xl font-bold">{t('admin.users.import.title')}</h1>

      <CreateNewUserDialog
        isOpen={isModalOpen}
        onOpenChange={(open) => {
          setIsModalOpen(open);
          if (!open) {
            navigate({
              to: '/users/import',
              search: (prev) => ({
                ...prev,
                openCreateContact: undefined,
                companyUniqueCode: undefined,
                companyName: undefined
              }),
              replace: true
            });
          }
        }}
        initialCompanyOption={initialCompanyOption}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="flex gap-2 items-center">
              <UserPlus className="w-6 h-6" />
              <p className="text-xl font-bold">{t('user.create.title')}</p>
            </h2>
            <p className="text-muted-foreground">{t('user.create.description')} </p>
          </div>
        </CardContent>

        <CardContent className="flex grow">
          <Button onClick={() => setIsModalOpen(true)} rounded="full">
            {t('user.create.button')} <Plus />
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h2 className="flex gap-2 items-center">
              <UserPlus className="w-6 h-6" />
              <p className="text-xl font-bold">{t('users.import.title')}</p>
            </h2>

            <p className="text-muted-foreground">
              <Trans i18nKey="users.import.description" components={{ 1: <br /> }} />
            </p>

            <CsvFile csv={csv} filename={filename} label={t('common.csv.example.button')} />
          </div>

          <FileUploader
            accept={{
              'text/csv': [],
              'application/vnd.ms-excel': [],
              'application/csv': [],
              'text/x-csv': [],
              'application/x-csv': [],
              'text/comma-separated-values': [],
              'text/x-comma-separated-values': []
            }}
            onParse={handleParse}
          />
        </CardContent>
      </Card>
    </div>
  );
}
